import React, { useRef, useEffect, useState, useCallback } from 'react';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';

const GooglePlacesAutocomplete = ({
  onPlaceSelect,
  value = '',
  className = '',
  required = false,
  apiKey = ''
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [inputValue, setInputValue] = useState(value);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  const updateMap = useCallback((lat, lng) => {
    if (!mapInstanceRef.current || !isMountedRef.current) return;

    try {
      const position = { lat, lng };
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(16);

      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }

      if (window.google?.maps) {
        markerRef.current = new window.google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          animation: window.google.maps.Animation.DROP
        });
      }
    } catch (error) {
      console.error('Error updating map:', error);
    }
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || !isMountedRef.current) {
      return;
    }

    try {
      const defaultCenter = { lat: -1.2921, lng: 36.8219 };
      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });

      mapInstanceRef.current = map;
      if (isMountedRef.current) {
        setIsMapLoaded(true);
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      if (isMountedRef.current) {
        setApiError(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      setApiError(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setApiError(false);

    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !isMountedRef.current) return;

        if (!inputRef.current || !window.google?.maps?.places) {
          setApiError(true);
          return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['establishment', 'geocode'],
          fields: ['formatted_address', 'geometry', 'name', 'place_id', 'address_components']
        });

        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          if (!isMountedRef.current) return;

          const place = autocomplete.getPlace();
          if (!place.geometry?.location) return;

          const locationData = {
            address: place.formatted_address || place.name,
            name: place.name || '',
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            placeId: place.place_id || '',
            addressComponents: place.address_components || []
          };

          setInputValue(locationData.address);
          updateMap(locationData.lat, locationData.lng);
          onPlaceSelect?.(locationData);
        });

        const timer = setTimeout(() => {
          if (isMountedRef.current && mapRef.current) {
            initializeMap();
          }
        }, 100);

        return () => clearTimeout(timer);
      })
      .catch(() => {
        if (isMountedRef.current) {
          setApiError(true);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (autocompleteRef.current && window.google?.maps) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch {
          // ignore cleanup errors
        }
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapInstanceRef.current = null;
      autocompleteRef.current = null;
    };
  }, [apiKey, initializeMap, updateMap, onPlaceSelect]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 pointer-events-none z-10">
          Location *
        </label>
        <div className="relative">
          <i className="fas fa-map-marker-alt absolute left-4 top-4 text-gray-400 pointer-events-none z-10"></i>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Search for a location..."
            className={`w-full py-3 pl-12 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all ${className}`}
            required={required}
            autoComplete="off"
            disabled={apiError && !loading}
          />
        </div>
      </div>

      <div className="relative">
        <div
          ref={mapRef}
          className="w-full h-80 rounded-lg border border-gray-300 overflow-hidden shadow-sm relative"
          style={{ minHeight: '320px' }}
        >
          {(apiError || loading) && (
            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 z-10 pointer-events-none">
              <div className="text-center p-4">
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
                    <p className="text-sm text-gray-600">Loading Google Maps…</p>
                  </>
                ) : (
                  <>
                    <i className="fas fa-exclamation-triangle text-2xl text-yellow-500 mb-2"></i>
                    <p className="text-sm text-gray-700 mb-1">Google Maps not available</p>
                    <p className="text-xs text-gray-500">Add your API key in Settings, or enter the location manually below</p>
                  </>
                )}
              </div>
            </div>
          )}
          {!isMapLoaded && !apiError && !loading && (
            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 z-10 pointer-events-none">
              <div className="text-center p-4">
                <i className="fas fa-map-marked-alt text-3xl text-gray-400 mb-2"></i>
                <p className="text-sm text-gray-500 mb-1">Select a location to see it on the map</p>
              </div>
            </div>
          )}
        </div>
        {isMapLoaded && !apiError && (
          <div className="absolute top-2 right-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-200 text-xs text-gray-600 z-20 pointer-events-none">
            <i className="fas fa-map-marked-alt mr-1"></i>
            Map View
          </div>
        )}
      </div>
    </div>
  );
};

export default GooglePlacesAutocomplete;
